import {Ok, Result} from 'oxide.ts';
import {getEntityVersions} from '../../rest/api';
import type {EntityType} from '../../@types/entity';
import type {EntityNames} from '../../entities';
import type {HttpError} from '../../lib/picos/http';

type CacheEntry<T> = {
    addedOn: number;
    versions: T[];
};

const cache = new Map<string, CacheEntry<any>>();

/**
 * Requests that have been sent and not yet answered, keyed by the cache key
 * plus the addedOn asked for. A caller asking for a version that is already on
 * its way waits for that request instead of sending another (OWA-961): the
 * Dashboard activity feed asks again on every re-render, and without this each
 * re-render sent a full new set until the user was rate limited. An entry
 * removes itself when its request settles, so this never holds more than the
 * requests in flight.
 */
const inFlight = new Map<string, Promise<Result<any[], HttpError>>>();

/**
 * Fetch entity versions with in-memory caching.
 * Cache is keyed by entityType-entityId, invalidated when entity.addedOn changes.
 */
export async function getVersionsCached<T>(entityType: EntityNames, entity: EntityType): Promise<Result<T[], HttpError>> {
    const key = `${entityType}-${entity.id}`;
    const cached = cache.get(key);

    if (cached && cached.addedOn === entity.addedOn) {
        return Ok(cached.versions);
    }

    const slot = `${key}@${entity.addedOn}`;
    const pending = inFlight.get(slot);

    if (pending) return pending;

    const request = getEntityVersions<T>(entityType, entity);
    inFlight.set(slot, request);

    let result: Result<T[], HttpError>;

    try {
        result = await request;
    }
    catch (err) {
        if (inFlight.get(slot) === request) inFlight.delete(slot);
        throw err;
    }

    // A request whose slot was dropped by an invalidation is answering a
    // question nobody is asking now: it must not touch the slot or the cache.
    if (inFlight.get(slot) !== request) return result;
    inFlight.delete(slot);

    // An older version that settles late must not replace a newer cached one.
    const newerCached = (cache.get(key)?.addedOn ?? -Infinity) > entity.addedOn;

    if (result.isOk() && !newerCached) {
        cache.set(key, {addedOn: entity.addedOn, versions: result.unwrap()});
    }

    return result;
}

/**
 * Invalidate the cache for a specific entity.
 */
export function invalidateVersionCache(entityType: string, entityId: string): void {
    const key = `${entityType}-${entityId}`;
    cache.delete(key);

    for (const slot of inFlight.keys()) {
        if (slot.startsWith(`${key}@`)) inFlight.delete(slot);
    }
}

/**
 * Clear the entire version cache.
 */
export function clearVersionCache(): void {
    cache.clear();
    inFlight.clear();
}
